import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useSubscription } from "@/hooks/useSubscription";
import { STRIPE_CONFIG } from "@/config/stripe";
import { 
  ArrowLeft, 
  Calendar, 
  CreditCard, 
  Crown, 
  Loader2, 
  RefreshCw,
  ExternalLink 
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { SettingsLayout } from "@/components/dashboard/SettingsLayout";

const SubscriptionSettings = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { 
    subscription, 
    isLoading, 
    currentPlan, 
    quickRefresh,
    openCustomerPortal 
  } = useSubscription();
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await quickRefresh();
    setIsRefreshing(false);
    toast({
      title: t("subscription.refreshed"),
      description: t("subscription.refreshedDesc"),
    });
  };

  const handleManageSubscription = async () => {
    try {
      setIsOpeningPortal(true);
      await openCustomerPortal();
    } catch (error) {
      console.error("Portal error:", error);
      toast({
        title: t("subscription.error"),
        description: t("subscription.portalError"),
        variant: "destructive",
      });
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const getPlanInfo = () => {
    if (currentPlan === "pro") {
      return {
        name: t("pricingPage.plans.pro.name"),
        icon: Crown,
        color: "text-yellow-500",
        bgColor: "bg-yellow-500/10",
      };
    }
    if (currentPlan === "basic") {
      return {
        name: t("pricingPage.plans.basic.name"),
        icon: Crown,
        color: "text-primary",
        bgColor: "bg-primary/10",
      };
    }
    return {
      name: t("pricingPage.plans.free.name"),
      icon: Crown,
      color: "text-muted-foreground",
      bgColor: "bg-muted",
    };
  };

  const planInfo = getPlanInfo();
  const PlanIcon = planInfo.icon;

  const getStatusBadge = () => {
    const status = subscription?.status || "none";
    switch (status) {
      case "active":
        return <Badge className="bg-green-500">{t("subscription.status.active")}</Badge>;
      case "trialing":
        return <Badge variant="secondary">{t("subscription.status.trialing")}</Badge>;
      case "past_due":
        return <Badge variant="destructive">{t("subscription.status.pastDue")}</Badge>;
      case "canceled":
        return <Badge variant="outline">{t("subscription.status.canceled")}</Badge>;
      default:
        return <Badge variant="outline">{t("subscription.status.none")}</Badge>;
    }
  };

  const getPriceDisplay = () => {
    if (currentPlan === "free") return t("subscription.free");
    
    const cycle = subscription?.billing_cycle || "monthly";
    const planConfig = STRIPE_CONFIG.products[currentPlan as "basic" | "pro"];
    
    const formatter = new Intl.NumberFormat(i18n.language, {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
    });
    
    if (cycle === "yearly") {
      return `${formatter.format(planConfig.yearly.monthlyEquivalent)}/${t("pricing.perMonth")} (${t("pricing.yearly")})`;
    }
    return `${formatter.format(planConfig.monthly.amount)}/${t("pricing.perMonth")}`;
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold">{t("subscription.settings.title")}</h1>
        <p className="text-muted-foreground">{t("subscription.settings.subtitle")}</p>
      </div>

      <SettingsLayout>
        <div className="space-y-6">
          {/* Current Plan Card */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                {isLoading ? (
                  <div className="flex items-center gap-3 animate-pulse">
                    <div className="w-12 h-12 rounded-full bg-muted" />
                    <div>
                      <div className="h-5 w-24 bg-muted rounded mb-2" />
                      <div className="h-4 w-16 bg-muted rounded" />
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-full ${planInfo.bgColor} flex items-center justify-center`}>
                      <PlanIcon className={`w-6 h-6 ${planInfo.color}`} />
                    </div>
                    <div>
                      <CardTitle className="flex items-center gap-2">
                        {planInfo.name}
                        {getStatusBadge()}
                      </CardTitle>
                      <CardDescription>{getPriceDisplay()}</CardDescription>
                    </div>
                  </div>
                )}
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleRefresh}
                  disabled={isRefreshing || isLoading}
                >
                  <RefreshCw className={`w-4 h-4 mr-2 ${isRefreshing ? "animate-spin" : ""}`} />
                  {t("subscription.refresh")}
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {isLoading ? (
                <div className="h-6 w-48 bg-muted rounded animate-pulse" />
              ) : (
                <>
                  {subscription?.current_period_end && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-muted-foreground" />
                      <span className="text-muted-foreground">
                        {subscription.cancel_at_period_end 
                          ? t("subscription.endsOn") 
                          : t("subscription.renewsOn")}
                      </span>
                      <span className="font-medium">
                        {format(new Date(subscription.current_period_end), "PPP")}
                      </span>
                    </div>
                  )}

                  {subscription?.cancel_at_period_end && (
                    <div className="p-3 bg-destructive/10 rounded-md text-sm text-destructive">
                      {t("subscription.cancelWarning")}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{t("subscription.actions.title")}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isLoading ? (
                <div className="space-y-3">
                  <div className="h-10 bg-muted rounded animate-pulse" />
                </div>
              ) : currentPlan === "free" ? (
                <Button 
                  className="w-full" 
                  onClick={() => navigate("/#pricing")}
                >
                  <Crown className="w-4 h-4 mr-2" />
                  {t("subscription.actions.upgrade")}
                </Button>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between"
                    onClick={() => navigate("/#pricing")}
                  >
                    <span>{t("subscription.actions.changePlan")}</span>
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    className="w-full justify-between"
                    onClick={handleManageSubscription}
                    disabled={isOpeningPortal}
                  >
                    <span className="flex items-center gap-2">
                      <CreditCard className="w-4 h-4" />
                      {t("subscription.actions.managePayment")}
                    </span>
                    {isOpeningPortal ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ExternalLink className="w-4 h-4" />
                    )}
                  </Button>

                  <Separator className="my-4" />

                  <Button 
                    variant="ghost" 
                    className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
                    onClick={handleManageSubscription}
                    disabled={isOpeningPortal}
                  >
                    {t("subscription.actions.cancel")}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* Help */}
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground text-center">
                {t("subscription.help.text")}{" "}
                <a 
                  href="mailto:support@catedward.info" 
                  className="text-primary hover:underline"
                >
                  {t("subscription.help.contact")}
                </a>
              </p>
            </CardContent>
          </Card>
        </div>
      </SettingsLayout>
    </div>
  );
};

export default SubscriptionSettings;
